/**
 * Annova ERP brand palette — a Microsoft Outlook / Fluent look: the recognisable
 * Outlook blue primary over a neutral Office-grey canvas. These values are shared
 * by the light and dark MUI themes and by any bespoke visualisations (ApexCharts, etc.).
 */
export const brand = {
  /** Outlook / Fluent brand blue. */
  primary: '#0F6CBD',
  primaryLight: '#2B88D8',
  primaryDark: '#0A4C88',
  /** A slightly deeper blue used for the app bar and accents. */
  secondary: '#106EBE',
  secondaryLight: '#2B88D8',
  secondaryDark: '#0A2A44',
  /** Fluent green used for positive/success states. */
  accent: '#107C10',
  /** Office neutral canvas. */
  background: '#F3F2F1',
  paper: '#FFFFFF',
} as const;

/**
 * The Office 2007 colour schemes, for the "Outlook 2007" theme. Light mode is the *Blue* scheme —
 * the pale-blue window frame, navy text, sky-blue gradient chrome and the orange selection
 * highlight that the era is remembered for. Dark mode is the *Black* scheme Office 2007 also
 * shipped: charcoal gradients with silver text and the same orange highlight.
 */
export const office2007 = {
  blue: {
    /** The window frame behind the panes. */
    frame: '#BFDBFF',
    pane: '#FFFFFF',
    /** Navy used for text on chrome, headings and links. */
    navy: '#15428B',
    navyLight: '#3A6BB5',
    navyDark: '#0F3168',
    text: '#1E1E1E',
    textSecondary: '#4D5F7A',
    border: '#8DB2E3',
    borderSoft: '#A9C1E4',
    /** Title bar / ribbon gradient, top to bottom. */
    chromeGradient: 'linear-gradient(180deg, #E3EFFF 0%, #C4DAF3 55%, #ADC9EB 100%)',
    chromeBase: '#C4DAF3',
    /** Group / table header gradient. */
    headerGradient: 'linear-gradient(180deg, #E4ECF7 0%, #D6E3F3 100%)',
    buttonGradient: 'linear-gradient(180deg, #4A78C2 0%, #15428B 100%)',
    /** The orange selection: hover and selected states. */
    selectionGradient: 'linear-gradient(180deg, #FFE7A2 0%, #FFD58D 45%, #FFC35A 100%)',
    selectionHoverGradient: 'linear-gradient(180deg, #FFF3C7 0%, #FFE7A2 45%, #FFD58D 100%)',
    selectionBorder: '#FFB700',
    selectionText: '#1E1E1E',
  },
  black: {
    frame: '#2F2F2F',
    pane: '#3C3C3C',
    navy: '#9DBCE8',
    navyLight: '#BBD2F0',
    navyDark: '#6E93C7',
    text: '#F0F0F0',
    textSecondary: '#BDBDBD',
    border: '#5F5F5F',
    borderSoft: '#4E4E4E',
    chromeGradient: 'linear-gradient(180deg, #5C5C5C 0%, #3B3B3B 55%, #2A2A2A 100%)',
    chromeBase: '#3B3B3B',
    headerGradient: 'linear-gradient(180deg, #575757 0%, #414141 100%)',
    buttonGradient: 'linear-gradient(180deg, #7A9FD6 0%, #4A78C2 100%)',
    selectionGradient: 'linear-gradient(180deg, #FFD58D 0%, #FFB700 100%)',
    selectionHoverGradient: 'linear-gradient(180deg, #FFE7A2 0%, #FFD58D 100%)',
    selectionBorder: '#FF9E1B',
    selectionText: '#1B1B1B',
  },
} as const;

/**
 * Red / Amber / Green (RAG) performance status colours used across dashboards,
 * scorecards and reports. Orange is included to match the four-band SBP model
 * (Green >= 100%, Amber 80-99%, Orange 50-79%, Red < 50%).
 */
export const ragColors = {
  green: '#2E7D32',
  amber: '#F9A825',
  orange: '#EF6C00',
  red: '#C62828',
  none: '#9E9E9E',
} as const;

/**
 * Lighter RAG shades tuned for legibility on the dark theme's near-black background, where the
 * default (light-mode) RAG colours are too dark to read.
 */
export const ragColorsDark = {
  green: '#5FD07A',
  amber: '#FFCA28',
  orange: '#FFA726',
  red: '#F26D6D',
  none: '#AEBDB2',
} as const;
